import React, { useEffect, useState, useCallback } from "react";
import { Card, Container, Button, Spinner, Alert, ListGroup, Badge, Row, Col, Collapse } from "react-bootstrap";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import { sendRequestStatusEmails } from "../services/emailNotifications";

const ES = {
  PENDIENTE: "pendiente",
  EN_REVISION: "en revisión",
  APROBADA: "aprobada",
  RECHAZADA: "rechazada",
  COMPLETADA: "completada",
};

const MedicationRequests = () => {
  const [myRequests, setMyRequests] = useState([]);         // como solicitante
  const [asDonorRequests, setAsDonorRequests] = useState([]); // como donante
  const [actionable, setActionable] = useState([]);          // para donante: pendiente / en revisión
  const [otherDonorReqs, setOtherDonorReqs] = useState([]);  // resto (aprobada, rechazada, completada)
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: "", variant: "success" });
  const [user, userLoading] = useAuthState(auth);
  const [updating, setUpdating] = useState({});
  const [showOther, setShowOther] = useState(false);

  const navigate = useNavigate();

  const normalize = useCallback((v) => (v ?? "").toString().trim().toLowerCase(), []);
  const toSpanishCanonical = useCallback((status) => {
    const s = normalize(status);
    if (["pending", "pendiente"].includes(s)) return ES.PENDIENTE;
    if (["under_review", "en revisión", "en revision", "bajo revisión", "bajo revision"].includes(s))
      return ES.EN_REVISION;
    if (["approved", "aprobada", "aprobado"].includes(s)) return ES.APROBADA;
    if (["rejected", "rechazada", "rechazado"].includes(s)) return ES.RECHAZADA;
    if (["completed", "completada", "completado"].includes(s)) return ES.COMPLETADA;
    return s || ES.PENDIENTE;
  }, [normalize]);

  const isActionable = useCallback((status) => {
    const s = toSpanishCanonical(status);
    return s === ES.PENDIENTE || s === ES.EN_REVISION;
  }, [toSpanishCanonical]);

  const getStatusBadge = (status) => {
    const s = toSpanishCanonical(status);
    if (s === ES.PENDIENTE) return "warning";
    if (s === ES.EN_REVISION) return "info";
    if (s === ES.APROBADA) return "success";
    if (s === ES.RECHAZADA) return "danger";
    if (s === ES.COMPLETADA) return "primary";
    return "secondary";
  };

  const pretty = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const isImageUrl = (url) => /\.(png|jpe?g|webp)(\?.*)?$/i.test(url || "");
  const renderPrescription = (req) => {
    if (!req.prescriptionUrl) return <span className="text-muted">No subida</span>;
    return isImageUrl(req.prescriptionUrl) ? (
      <>
        <img
          src={req.prescriptionUrl}
          alt={req.prescriptionName || "Receta"}
          className="img-fluid img-thumbnail mt-2"
          style={{ maxHeight: 180, objectFit: "contain" }}
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
        <div className="mt-2">
          <Button as="a" href={req.prescriptionUrl} target="_blank" rel="noreferrer" variant="outline-primary" size="sm">
            Abrir receta
          </Button>
        </div>
      </>
    ) : (
      <Button as="a" href={req.prescriptionUrl} target="_blank" rel="noreferrer" variant="outline-primary" size="sm">
        {req.prescriptionName || "Ver receta"}
      </Button>
    );
  };

  const formatDate = (val) => {
    try {
      const d = val?.toDate ? val.toDate() : new Date(val);
      if (Number.isNaN(d?.getTime?.())) return "Fecha no disponible";
      return d.toLocaleDateString("es-ES", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Fecha no disponible";
    }
  };

  // Redirigir si no autenticado
  useEffect(() => {
    if (!userLoading && !user) navigate("/login");
  }, [user, userLoading, navigate]);

  // Cargar solicitudes del usuario como solicitante y como donante
  useEffect(() => {
    const load = async () => {
      if (!user || userLoading) return;
      try {
        setLoading(true);

        // Como solicitante
        const qMine = query(collection(db, "requests"), where("userId", "==", user.uid));
        const mineSnap = await getDocs(qMine);
        const mine = mineSnap.docs.map((d) => ({ id: d.id, ...d.data(), status: toSpanishCanonical(d.data().status) }));

        // Como donante
        const qDonor = query(collection(db, "requests"), where("donorId", "==", user.uid));
        const donorSnap = await getDocs(qDonor);
        const donor = donorSnap.docs.map((d) => ({ id: d.id, ...d.data(), status: toSpanishCanonical(d.data().status) }));

        setMyRequests(mine);
        setAsDonorRequests(donor);

        // Particionar donante
        const act = donor.filter((r) => isActionable(r.status));
        const others = donor.filter((r) => !isActionable(r.status));
        setActionable(act);
        setOtherDonorReqs(others);
      } catch (err) {
        console.error(err);
        setAlert({ show: true, message: "Error al cargar las solicitudes.", variant: "danger" });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, userLoading, isActionable, toSpanishCanonical]);

  // Aprobar / Rechazar como donante
  const sendNotification = async (request, spanishStatus) => {
    try {
      const statusText = spanishStatus.toUpperCase();
      await addDoc(collection(db, "notifications"), {
        userId: request.userId,
        requestId: request.id,
        type: "request_update",
        message: `Tu solicitud de ${request.medicationName} ha sido ${statusText}`,
        status: spanishStatus,
        read: false,
        createdAt: serverTimestamp(),
        donorName: request.donorName || user?.displayName || "Un donante",
        medicationName: request.medicationName,
        medicationId: request.medicationId,
        quantity: request.quantity || 1,
      });
    } catch (e) {
      console.error("Error enviando notificación:", e);
    }
  };

  const handleRequest = async (requestId, nextStatusES) => {
    const normalized = toSpanishCanonical(nextStatusES); // "aprobada" | "rechazada"
    try {
      setUpdating((p) => ({ ...p, [requestId]: true }));
      await updateDoc(doc(db, "requests", requestId), {
        status: normalized,
        updatedAt: serverTimestamp(),
      });

      // Refrescar local en ambas vistas
      const updDonor = asDonorRequests.map((r) => (r.id === requestId ? { ...r, status: normalized } : r));
      setAsDonorRequests(updDonor);
      setActionable(updDonor.filter((r) => isActionable(r.status)));
      setOtherDonorReqs(updDonor.filter((r) => !isActionable(r.status)));

      const updMine = myRequests.map((r) => (r.id === requestId ? { ...r, status: normalized } : r));
      setMyRequests(updMine);

      // Notificar al solicitante
      const full = asDonorRequests.find((r) => r.id === requestId);
      if (full) {
        await sendNotification(full, normalized);
        await sendRequestStatusEmails({
          requestId,
          medicationName: full.medicationName,
          status: normalized,
          requesterEmail: full.userEmail,
          donorEmail: full.donorEmail || user?.email,
          actorRole: "donante",
        });
      }

      setAlert({
        show: true,
        message: `Solicitud ${normalized === ES.APROBADA ? "aprobada" : "rechazada"} correctamente`,
        variant: normalized === ES.APROBADA ? "success" : "info",
      });
    } catch (err) {
      console.error(err);
      setAlert({ show: true, message: "Error al procesar la solicitud.", variant: "danger" });
    } finally {
      setUpdating((p) => ({ ...p, [requestId]: false }));
    }
  };

  if (!user || userLoading || loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando...</span>
        </Spinner>
      </Container>
    );
  }

  const renderDonorItem = (req) => {
    const medImg = req.medicationImageUrl || null;
    return (
      <ListGroup.Item key={req.id} className="mb-3">
        <Row>
          {medImg && (
            <Col md={3} className="mb-3 mb-md-0 text-center">
              <img
                src={medImg}
                alt={req.medicationName}
                className="img-fluid img-thumbnail"
                style={{ maxHeight: 200, objectFit: "contain" }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            </Col>
          )}
          <Col md={medImg ? 9 : 12}>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h5 className="mb-2">{req.medicationName}</h5>
                <Row>
                  <Col md={6}>
                    <p className="mb-1"><strong>Categoría:</strong> {req.medicationCategory || "No especificada"}</p>
                    <p className="mb-1"><strong>Dosis:</strong> {req.medicationDosage || "No especificada"}</p>
                    <p className="mb-1"><strong>Condición:</strong> {req.medicationCondition || "No especificada"}</p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1"><strong>Cantidad:</strong> {req.medicationQuantity ?? "No especificada"}</p>
                    <p className="mb-1"><strong>Vencimiento:</strong> {req.medicationExpirationDate || "No especificada"}</p>
                    <p className="mb-1"><strong>Fecha solicitud:</strong> {formatDate(req.createdAt)}</p>
                  </Col>
                </Row>
                {req.requesterNotes && <p className="mt-2"><strong>Notas del solicitante:</strong> {req.requesterNotes}</p>}
                <div className="mt-2">
                  <strong>Receta:</strong> {renderPrescription(req)}
                </div>

                {isActionable(req.status) && (
                  <div className="d-flex gap-2 mt-3">
                    <Button
                      variant="success"
                      size="sm"
                      disabled={!!updating[req.id]}
                      onClick={() => handleRequest(req.id, ES.APROBADA)}
                    >
                      {updating[req.id] ? "Guardando…" : "Aprobar"}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={!!updating[req.id]}
                      onClick={() => handleRequest(req.id, ES.RECHAZADA)}
                    >
                      {updating[req.id] ? "Guardando…" : "Rechazar"}
                    </Button>
                  </div>
                )}
              </div>
              <Badge bg={getStatusBadge(req.status)} className="fs-6">{pretty(req.status)}</Badge>
            </div>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  const renderMyItem = (req) => (
    <Card key={req.id} className="mb-3">
      <Card.Body>
        <Card.Title className="d-flex justify-content-between align-items-center">
          <span>Solicitud #{req.id.slice(0, 8)}</span>
          <Badge bg={getStatusBadge(req.status)}>{pretty(req.status)}</Badge>
        </Card.Title>
        <Card.Text className="mb-2">
          <strong>Medicamento:</strong> {req.medicationName}
          <br />
          <strong>Dosis:</strong> {req.medicationDosage || "No especificada"}
          <br />
          <strong>Fecha:</strong> {formatDate(req.createdAt)}
          <br />
          <strong>Receta:</strong>{" "}
          {req.prescriptionUrl ? (
            <a href={req.prescriptionUrl} target="_blank" rel="noopener noreferrer">
              Ver receta
            </a>
          ) : (
            "No cargada"
          )}
        </Card.Text>
        {req.requesterNotes && <div><strong>Notas:</strong> {req.requesterNotes}</div>}
      </Card.Body>
    </Card>
  );

  const isDonorView = asDonorRequests.length > 0;

  return (
    <Container className="mt-4">
      <h1 className="text-center my-4">{isDonorView ? "Solicitudes Recibidas" : "Mis Solicitudes"}</h1>

      {alert.show && <Alert variant={alert.variant} onClose={() => setAlert({ ...alert, show: false })} dismissible>{alert.message}</Alert>}

      {isDonorView ? (
        <>
          {/* Por aprobar */}
          {actionable.length === 0 ? (
            <Card className="mb-4">
              <Card.Body className="text-center">
                <Card.Text className="fs-5 text-muted">No tienes solicitudes por aprobar</Card.Text>
              </Card.Body>
            </Card>
          ) : (
            <>
              <h4 className="mb-3">Por aprobar</h4>
              <ListGroup className="mb-4">
                {actionable.map(renderDonorItem)}
              </ListGroup>
            </>
          )}

          {/* Otras */}
          {otherDonorReqs.length > 0 && (
            <>
              <Button
                variant="outline-secondary"
                onClick={() => setShowOther(!showOther)}
                className="mb-3"
              >
                {showOther ? "Ocultar" : "Mostrar"} otras solicitudes ({otherDonorReqs.length})
              </Button>
              <Collapse in={showOther}>
                <div>
                  <h4 className="mb-3">Otras Solicitudes</h4>
                  <ListGroup>
                    {otherDonorReqs.map(renderDonorItem)}
                  </ListGroup>
                </div>
              </Collapse>
            </>
          )}
        </>
      ) : (
        // Vista solicitante
        <>
          {myRequests.length === 0 ? (
            <p className="text-center">No hay solicitudes.</p>
          ) : (
            <div className="row">
              {myRequests.map((req) => (
                <div key={req.id} className="col-md-6">
                  {renderMyItem(req)}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Container>
  );
};

export default MedicationRequests;
